import type { Id } from '@hq/utils';
import { toQuery, type HttpClient } from '../client';

// ---------------- 需求预测 ----------------

/** 预测周期类型（后端 `wl_forecast.period_type`）。 */
export type ForecastPeriodType = 'MONTH' | 'WEEK' | 'DAY';

export interface ForecastRow {
  id: Id;
  orgId: Id;
  materialId: Id;
  materialName: string | null;
  materialCode: string | null;
  /** 预测所属期间，如 `2026-11`。 */
  forecastPeriod: string | null;
  periodType: ForecastPeriodType | string | null;
  forecastQty: number;
  forecastAmount: number;
  /** 置信度 0~1（按历史有效天数占比推导）。 */
  confidence: number;
  /** 算法标识（当前为规则版 running-average）。 */
  method: string | null;
  availableQty: number;
  /** 建议采购量 = max(0, 预测需求 - 可用库存)。 */
  suggestPurchaseQty: number;
  generatedAt: string | null;
}

export interface ForecastRunRequest {
  /** 回看天数，默认 90，上限 365。 */
  historyDays?: number;
  /** 预测天数，默认 30，上限 180。 */
  forecastDays?: number;
  periodType?: ForecastPeriodType;
}

// ---------------- 降本推荐 ----------------

export interface RecommendRow {
  id: Id;
  orgId: Id;
  /** 被替代的原物资。 */
  materialId: Id;
  materialName: string | null;
  /** 推荐替代物资。 */
  recommendMaterialId: Id;
  recommendName: string | null;
  recommendType: string | null;
  currentPrice: number;
  targetPrice: number;
  /** 降本幅度 0~1。 */
  saveRatio: number;
  annualQty: number;
  annualSaveAmount: number;
  status: string | null;
  /** DR-13.9 依据（`{ bizNos[], metricDefVersion, dataSource, generatedAt }`）。 */
  evidenceJson: string | null;
  generateAt: string;
}

export type RecommendDecision = 'ADOPT' | 'REJECT';

export interface RecommendConfirmRequest {
  action?: RecommendDecision;
  opinion?: string;
}

// ---------------- 结论依据（DR-13.9） ----------------

export interface EvidenceTraceQuery {
  /** 目前仅支持 `WL_APPLY`。 */
  bizType?: 'WL_APPLY';
  bizNo?: string;
  bizId?: Id;
}

export interface EvidenceTraceResult {
  trace: {
    bizNo: string;
    bizType: string;
    bizStatus: string;
    totalAmount: number;
    totalQty: number;
    urgentLevel: string | null;
    allocateResult: string | null;
    slaDeadline: string | null;
    submitAt: string | null;
    approveAt: string | null;
    closeAt: string | null;
    instanceId: Id;
  };
  apply: Record<string, unknown> & { bizNo?: string; applyDeptName?: string | null; applyUserName?: string | null };
  items: EvidenceItemRow[];
  approval: {
    instance: Record<string, unknown> | null;
    tasks: EvidenceTaskRow[];
    actions: EvidenceActionRow[];
  };
  logistics: { deliveries: EvidenceDeliveryRow[]; tracks: EvidenceTrackRow[] };
  cost: { transfers: EvidenceCostRow[] };
  settlement: { items: EvidenceSettleItemRow[] };
  budget: EvidenceBudgetRow[];
  alerts: EvidenceAlertRow[];
}

export interface EvidenceItemRow {
  lineNo: number;
  materialCode: string | null;
  materialName: string | null;
  spec: string | null;
  unitName: string | null;
  reqQty: number;
  approvedQty: number;
  price: number;
  amount: number;
  allocateResult: string | null;
  outerAllocateQty: number;
  stockShortQty: number;
  signedQty: number;
  issuedQty: number;
}

export interface EvidenceTaskRow {
  nodeCode: string;
  nodeName: string;
  nodeType: string | null;
  seq: number;
  assigneeName: string | null;
  taskStatus: string;
  action: string | null;
  handleAt: string | null;
  opinion: string | null;
}

export interface EvidenceActionRow {
  nodeCode: string;
  fromStatus: string | null;
  toStatus: string | null;
  action: string;
  actorName: string | null;
  opinion: string | null;
  actAt: string | null;
}

export interface EvidenceDeliveryRow {
  id: Id;
  bizNo: string;
  deliveryType: string | null;
  deliveryStatus: string;
  totalQty: number;
  totalAmount: number;
  shipAt: string | null;
  arriveAt: string | null;
  signAt: string | null;
  isOvertime: boolean;
}

export interface EvidenceTrackRow {
  deliveryId: Id;
  trackType: string;
  trackDesc: string | null;
  location: string | null;
  occurredAt: string;
  terminal: string | null;
}

export interface EvidenceCostRow {
  id: Id;
  bizNo: string;
  triggerSource: string | null;
  accountPeriod: string | null;
  transferStatus: string;
  totalQty: number;
  totalAmount: number;
  costDeptName: string | null;
  sourceBizNo: string | null;
  settleId: Id;
}

export interface EvidenceSettleItemRow {
  bizNo: string | null;
  feeType: string | null;
  amount: number;
  settleNo: string;
  settlePeriod: string | null;
  settleStatus: string | null;
  payableAmount: number;
  dueDate: string | null;
}

export interface EvidenceBudgetRow {
  budgetId: Id;
  budgetItemId: Id;
  usageType: string | null;
  amount: number;
  afterUsed: number;
  afterFrozen: number;
  execRateSnapshot: number;
  overBudgetFlag: boolean;
  bizDate: string | null;
}

export interface EvidenceAlertRow {
  abnormalType: string | null;
  abnormalLevel: string | null;
  abnormalDesc: string | null;
  abnormalStatus: string | null;
  detectedAt: string | null;
}

/**
 * AI 中台（规则版，可被模型替换）：需求预测、降本推荐与「查看依据」链路。
 * 预测/推荐写接口需 `wl:purchase:manage`；依据查询需 `wl:analytics:view`。
 */
export function createAiInsightsApi(http: HttpClient) {
  return {
    forecast(period?: string): Promise<ForecastRow[]> {
      return http.request<ForecastRow[]>({ path: '/wl/ai/forecast', query: toQuery({ period }) });
    },

    /** 重算预测（按机构覆盖当期结果）。 */
    runForecast(body: ForecastRunRequest = {}): Promise<unknown> {
      return http.request<unknown>({ path: '/wl/ai/forecast/run', method: 'POST', body });
    },

    recommend(status?: string): Promise<RecommendRow[]> {
      return http.request<RecommendRow[]>({ path: '/wl/ai/recommend', query: toQuery({ status }) });
    },

    runRecommend(): Promise<unknown> {
      return http.request<unknown>({ path: '/wl/ai/recommend/run', method: 'POST', body: {} });
    },

    /** 采购人二次确认：ADOPT 采纳 / REJECT 驳回（采纳不自动改价，仍需走调价申请）。 */
    confirmRecommend(id: Id, body: RecommendConfirmRequest): Promise<unknown> {
      return http.request<unknown>({ path: `/wl/ai/recommend/${id}/confirm`, method: 'POST', body });
    },

    /** 查看依据：按 bizNo/bizId 反查申领全链路原始单据（申请→审批→判源→配送→成本→结算→预算）。 */
    evidence(query: EvidenceTraceQuery): Promise<EvidenceTraceResult> {
      return http.request<EvidenceTraceResult>({ path: '/wl/evidence', query: toQuery(query) });
    }
  };
}

export type AiInsightsApi = ReturnType<typeof createAiInsightsApi>;
